import { useEffect, useRef, useState } from 'react';

/** The attribute an in-place confirmation's opener carries, so focus can find it again. */
export const OPENER_ATTRIBUTE = 'data-confirm-opener';

/**
 * Returns focus to the opener once an in-place confirmation closes (DLG-004). The confirmation
 * replaced the content holding the opener, so the element is found again by its
 * `data-confirm-opener` key after the content re-renders. A removed opener (the row is gone) is
 * simply not found.
 */
export function useReturnFocus(open: boolean, openerKey: string | null) {
  const wasOpen = useRef(false);
  // Moving DOM focus is an external side effect of the close, so it runs after the commit.
  useEffect(() => {
    if (open) {
      wasOpen.current = true;
      return;
    }
    if (!wasOpen.current) return;
    wasOpen.current = false;
    if (openerKey === null) return;
    document
      .querySelector<HTMLElement>(`[${OPENER_ATTRIBUTE}="${CSS.escape(openerKey)}"]`)
      ?.focus();
  }, [open, openerKey]);
}

export interface ConfirmState<T> {
  /** The object awaiting confirmation; the dialog is open while it is non-null. */
  target: T | null;
  busy: boolean;
  /** The inline error of the last attempt (TMM-B3), or null. */
  error: string | null;
  /**
   * Opens the confirmation. `openerKey` (the opener's `data-confirm-opener` value) is for an
   * in-place confirmation, whose dismissal re-finds the opener and focuses it.
   */
  ask: (target: T, openerKey?: string) => void;
  /** Dismisses the confirmation — ignored while busy. */
  cancel: () => void;
  /**
   * Runs `action` on the target: success closes the dialog; a failure keeps it open with
   * `describe(error)` inline, or closes it when `describe` returns null (the caller has moved
   * the page to another state, e.g. the Subscribe card).
   */
  run: (
    action: (target: T) => Promise<void>,
    describe: (error: unknown) => string | null,
  ) => Promise<void>;
}

/** The pending-confirmation state behind a `ConfirmDialog` (TMMAPP-0071). */
export function useConfirm<T>(): ConfirmState<T> {
  const [target, setTarget] = useState<T | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openerKey, setOpenerKey] = useState<string | null>(null);
  useReturnFocus(target !== null, openerKey);

  return {
    target,
    busy,
    error,
    ask(next, key) {
      setTarget(next);
      setError(null);
      setOpenerKey(key ?? null);
    },
    cancel() {
      if (busy) return;
      setTarget(null);
      setError(null);
    },
    async run(action, describe) {
      if (target === null || busy) return;
      setBusy(true);
      setError(null);
      try {
        await action(target);
        setTarget(null);
      } catch (caught) {
        const message = describe(caught);
        if (message === null) setTarget(null);
        else setError(message);
      } finally {
        setBusy(false);
      }
    },
  };
}
